'use client';

import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { Field } from '../../components/field';
import { IconButton } from '../../components/icon-button';
import { SingleDatePicker } from '../inputs/SingleDatePicker';
import { HStack, Stack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

type CreatedAtDateFilterDialogProps = {
  isOpen: boolean;
  startDate: Date | null;
  endDate: Date | null;
  errorMessage: string | null;
  onOpenChange: ({ open }: { open: boolean }) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onCancel: () => void;
  onApply: () => void;
};

export function CreatedAtDateFilterDialog({
  isOpen,
  startDate,
  endDate,
  errorMessage,
  onOpenChange,
  onStartDateChange,
  onEndDateChange,
  onCancel,
  onApply,
}: CreatedAtDateFilterDialogProps) {
  const t = useTranslations('designSystem.createdAtDateFilter.customRange');
  const tCommon = useTranslations('designSystem.common');
  const [openCycle, setOpenCycle] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setOpenCycle((previousValue) => previousValue + 1);
  }, [isOpen]);

  return (
    <LegacyDialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="lg" w="full" position="relative">
          <Stack gap="6" p="6">
            <VStack gap="1" alignItems="flex-start">
              <LegacyDialog.Title>{t('title')}</LegacyDialog.Title>
              <LegacyDialog.Description>
                {t('description')}
              </LegacyDialog.Description>
            </VStack>

            <VStack gap={4} w="full" alignItems="stretch">
              <Field.Root>
                <Field.Label fontSize="xs" fontWeight="normal">
                  {t('startDate')}
                </Field.Label>
                <SingleDatePicker
                  key={`start-${openCycle}`}
                  value={startDate ? startDate.toISOString() : ''}
                  onChange={onStartDateChange}
                  placeholder={t('startDate')}
                  size="md"
                  width="100%"
                  disableFutureDates
                />
              </Field.Root>

              <Field.Root invalid={Boolean(errorMessage)}>
                <Field.Label fontSize="xs" fontWeight="normal">
                  {t('endDate')}
                </Field.Label>
                <SingleDatePicker
                  key={`end-${openCycle}`}
                  value={endDate ? endDate.toISOString() : ''}
                  onChange={onEndDateChange}
                  placeholder={t('endDate')}
                  size="md"
                  width="100%"
                  disableFutureDates
                />
                {errorMessage ? (
                  <Field.ErrorText>{errorMessage}</Field.ErrorText>
                ) : null}
              </Field.Root>
            </VStack>

            <HStack gap="3" justifyContent="flex-end" width="full">
              <Button variant="outline" onClick={onCancel}>
                {t('cancel')}
              </Button>
              <Button onClick={onApply}>{tCommon('apply')}</Button>
            </HStack>
          </Stack>
          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <IconButton aria-label={tCommon('close')} variant="ghost" size="sm">
              <Cancel01Icon />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
